import * as React from 'react';

import { cn } from '@shared/lib';

// TRAD-2099 W-14 — `src/shared/ui`에 체크박스류 공용 컴포넌트가 없었다(Switch.tsx만 있음 —
// ls로 확인, UI 룰1 "기존에 동일한 것 없음" 근거. 팀장 확정 2026-09-12). 텍스트 입력 모양인
// `Input`(h-9 border rounded-md px-3 py-1)은 체크박스와 형태가 안 맞아 새로 만든다.
//
// 기준 규격 = `Switch.tsx`(포커스링·disabled·크기 토큰): focus-visible 링 2px + offset,
// disabled:opacity-50, semantic 토큰만 사용(하드코딩 hex 없음, 라이트/다크 자동 대응).
// 네이티브 `<input type="checkbox">`를 래핑한다(Radix 의존 없음 — Switch와 달리 Radix
// checkbox 패키지가 설치돼 있지 않고, 네이티브 체크박스로 충분한 단순 on/off라 새 의존성을
// 추가하지 않는다).
//
// label 슬롯: 기존 4개 소비처(TermsAgreement·SignUp·subscription-score-page·기타)가 전부
// `<label>{checkbox}{텍스트}</label>` 형태로 손으로 감싸고 있었다 — 그 구조를 컴포넌트 안으로
// 가져와 `label` prop(ReactNode — SignUp.tsx는 약관/개인정보 링크가 섞인 리치 텍스트라 문자열
// 고정 불가)으로 받는다. `label`을 안 넘기면 순수 `<input>`만 렌더(래퍼 없이 직접 배치하고
// 싶은 자리를 위해 남겨둔다).

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** 체크박스 옆에 렌더할 라벨. ReactNode라 링크가 섞인 리치 텍스트도 받는다. 생략하면 label 없이 input만 렌더한다. */
  label?: React.ReactNode;
  /** label 텍스트(span) 자체에 추가로 줄 클래스 — 소비처마다 text-xs/text-sm 등 크기가 다르다. */
  labelClassName?: string;
  /** input+label을 감싸는 <label> 엘리먼트에 줄 클래스 — 소비처마다 gap·정렬·마진이 다르다. */
  containerClassName?: string;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, labelClassName, containerClassName, id, disabled, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;

    const input = (
      <input
        type="checkbox"
        id={inputId}
        disabled={disabled}
        // 🔴 TRAD-3778 — 네이티브 모양(appearance:auto)은 우리 토큰을 안 따른다: 다크에서도 미체크가 흰 채움 칸이었다
        // (실측 rgb(255,255,255), color-scheme=normal). `border-*`·`rounded`·`h-4` 도 무시돼 20×20 으로 그려졌다.
        // appearance-none 으로 직접 그린다 — 미체크 = 투명 채움 + 테두리(디자인 감사 2026-10-19 §4 «web 은 테두리만(투명 채움)»),
        // 체크 = theme-primary 채움 + 흰 체크 표시(`.checkbox-tick`, index.css).
        className={cn(
          'checkbox-tick h-4 w-4 shrink-0 cursor-pointer appearance-none rounded border border-muted-foreground bg-transparent transition-colors checked:border-theme-primary checked:bg-theme-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        ref={ref}
        {...props}
      />
    );

    if (label == null) return input;

    return (
      <label
        htmlFor={inputId}
        className={cn('inline-flex items-center gap-2 cursor-pointer', disabled && 'cursor-not-allowed', containerClassName)}
      >
        {input}
        <span className={labelClassName}>{label}</span>
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
